import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderOpen,
  Users,
  FileText,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronDown,
  ChevronRight,
  Globe,
  Building2,
  UserCircle,
  Calculator,
} from 'lucide-react';
import { Button } from './ui/button';

const HRLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [catalogExpanded, setCatalogExpanded] = useState(true);
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const handleSwitchToAccounting = () => {
    window.location.href = '/dashboard';
  };

  const menuItems = [
    {
      name: 'Catálogo',
      icon: FolderOpen,
      expandable: true,
      expanded: catalogExpanded,
      onToggle: () => setCatalogExpanded(!catalogExpanded),
      subItems: [
        { name: 'General', href: '/rrhh/catalogo/general', icon: Globe },
        { name: 'Empresa', href: '/rrhh/catalogo/empresa', icon: Building2 },
      ]
    },
    { name: 'Empleados', href: '/rrhh/empleados', icon: Users },
    { name: 'Movimientos', href: '/rrhh/movimientos', icon: Calculator },
    { name: 'Reportes', href: '/rrhh/reportes', icon: BarChart3 },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-gradient-to-r from-purple-900 to-purple-800 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCircle className="h-6 w-6" />
          <span className="font-bold">RRHH</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-white"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 
        bg-gradient-to-b from-purple-900 to-purple-800
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-purple-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
              <UserCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">Sistema RRHH</h1>
              <p className="text-purple-300 text-xs">Express Products</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <div key={item.name}>
              {item.expandable ? (
                <>
                  <button
                    onClick={item.onToggle}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-purple-200 hover:bg-purple-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </div>
                    {item.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {item.expanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.href}
                          to={subItem.href}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                            isActive(subItem.href)
                              ? 'bg-purple-600 text-white'
                              : 'text-purple-300 hover:bg-purple-700/50 hover:text-white'
                          }`}
                        >
                          <subItem.icon className="h-4 w-4" />
                          <span className="text-sm">{subItem.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-purple-600 text-white'
                      : 'text-purple-200 hover:bg-purple-700/50 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-purple-700">
          <div className="mb-3 px-4">
            <p className="text-white font-medium text-sm truncate">{user.full_name || 'Usuario'}</p>
            <p className="text-purple-300 text-xs truncate">{user.email}</p>
          </div>
          
          {user.has_accounting_access && (
            <Button
              variant="ghost"
              className="w-full justify-start text-purple-200 hover:text-white hover:bg-purple-700/50 mb-2"
              onClick={handleSwitchToAccounting}
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Ir a Contabilidad
            </Button>
          )}
          
          <Button
            variant="ghost"
            className="w-full justify-start text-purple-200 hover:text-white hover:bg-purple-700/50"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default HRLayout;
